import * as React from 'react'
import EditPageContainer from '@app/components/EditPageContainer'
import PropertyEdit from '@app/components/PropertyEdit'
import ListingEdit from '@app/components/ListingEdit'
import AssociatedPropertySelector from '@app/components/AssociatedPropertySelector'
import ListingSelector from '@app/components/ListingSelector'
import { Button, Container } from 'reactstrap'
import { AssociatedProperty, IAssociatedProperty } from '@app/models/AssociatedProperty'
import { actionCreators as propertyActionCreators } from '@app/components/PropertyEdit/modules/propertyEdit'
import { actionCreators as pipelineActionCreators } from '@app/components/PipelineEdit/modules/pipelineEdit'
import { IListingEditFiles, actionCreators as listingActionCreators, selectFlyerFiles, selectMarketingPackageFiles } from '@app/components/ListingEdit/modules/listingEdit'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from '@app/store'
import PipelineEdit from '@app/components/PipelineEdit'
import { IRequestOptions, makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { Attachment } from '@app/models/Attachment'
import { selectFiles } from '@app/components/FileUpload/modules/fileUpload'
import { errorToast, successToast } from '@app/utils/notifications'
import { createStructuredSelector } from 'reselect/lib'
import { fromJS, List } from 'immutable'
import { goBack, push } from 'connected-react-router'
import { Listing, IListing } from '@app/models/Listing'
import { BrokeragePipeline } from '@app/models/BrokeragePipeline'

const PHOTOS_IDENTIFIER = 'property-photo-editor'
const ATTACHMENTS_IDENTIFIER = 'property-attachment-editor'

export const PipelineQuickAdd = () => {
  const [isSaving, setIsSaving] = React.useState(false)
  const [selectedProperty, setSelectedProperty] = React.useState<IAssociatedProperty>()
  const [createProperty, setCreateSelected] = React.useState(false)
  const [selectedListing, setSelectedListing] = React.useState<IListing>()
  const [createListing, setCreateListing] = React.useState(false)

  React.useEffect(() => {
    if (selectedProperty) {
      dispatch(propertyActionCreators.loadEditProperty(selectedProperty.propertyId))
      setSelectedListing(undefined)
      setCreateListing(false)
    }
  }, [selectedProperty])

  const listingState = useSelector((state: ApplicationState) => state.listingEdit.listing)
  const pipelineState = useSelector((state: ApplicationState) => state.brokeragePipelineEdit.pipeline)
  const propertyState = useSelector((state: ApplicationState) => state.propertyEdit.property)
  const photos = useSelector((state: ApplicationState) => selectFiles(PHOTOS_IDENTIFIER)(state))
  const files: IListingEditFiles = useSelector(
    createStructuredSelector<ApplicationState, IListingEditFiles>({
      flyerFiles: selectFlyerFiles(),
      marketingPackageFiles: selectMarketingPackageFiles(),
    })
  )

  const attachments = useSelector((state: ApplicationState) => selectFiles(ATTACHMENTS_IDENTIFIER)(state))

  const dispatch = useDispatch()

  const setPhotosAndAttachments = (state: any, skipPhotos = false) => {
    const photosList = photos?.toArray().reduce((list, fileUpload) => {
      if (fileUpload === undefined || fileUpload.error) {
        return list
      }

      return list.push(fileUpload.attachment)
    }, List<Attachment>())

    const attachmentsList = attachments?.toArray().reduce((list, fileUpload) => {
      if (fileUpload === undefined || fileUpload.error) {
        return list
      }

      return list.push(fileUpload.attachment)
    }, List<Attachment>())

    return skipPhotos ? state.set('attachments', attachmentsList) : state.set('photos', photosList).set('attachments', attachmentsList)
  }

  const saveProperty = async (propertyId?: string) => {
    let updatedPropertyState = setPhotosAndAttachments(propertyState)

    let result;
    if (propertyId) {
      result = await makeAuthenticatedRequest(`/api/Properties/${propertyId}`, {
        method: RequestMethod.PUT,
        body: JSON.stringify(updatedPropertyState.toJS()),
      })
    } else {
      updatedPropertyState = updatedPropertyState.set('quickAdd', true)
      result = await makeAuthenticatedRequest('api/Properties', {
        method: RequestMethod.POST,
        body: JSON.stringify(updatedPropertyState.toJS()),
      })
    }

    if (result.success) {
      return propertyId ?? result.entity.id
    } else {
      console.error('Failed to save property:', result.message)
      errorToast('Oops!', result.message || 'Failed to save')
    }
  }

  const saveListing = async (propertyId: string, listingId?: string) => {
    let updatedListingState = listingState.setIn(['property', 'id'], propertyId) as Listing

    updatedListingState = updatedListingState.set('flyer', files.flyerFiles?.first()?.attachment.toJS()) as Listing

    if (updatedListingState.flyer) {
      updatedListingState = updatedListingState.set('flyerId', updatedListingState.flyer.id) as Listing
    }

    updatedListingState = updatedListingState.set(
      'marketingPackage',
      files.marketingPackageFiles && files.marketingPackageFiles.size > 0 ? files.marketingPackageFiles.first().attachment.toJS() : undefined
    ) as Listing

    if (updatedListingState.marketingPackage) {
      updatedListingState = updatedListingState.set('marketingPackageId', updatedListingState.marketingPackage.id) as Listing
    }

    updatedListingState = setPhotosAndAttachments(updatedListingState)

    let result;

    if (listingId) {
      result = await makeAuthenticatedRequest(`/api/Listings/${listingId}`, {
        method: RequestMethod.PUT,
        body: JSON.stringify(updatedListingState.toJS()),
      })
    } else {
      updatedListingState = updatedListingState.set('quickAdd', true) as Listing
      result = await makeAuthenticatedRequest('/api/Listings', {
        method: RequestMethod.POST,
        body: JSON.stringify(updatedListingState.toJS()),
      })
    }

    if (result.success === true) {
      return listingId ?? result.entity.id
    } else {
      dispatch(errorToast('Oops!', result.message))
    }
  }

  const savePipeline = async (listingId: string) => {
    try {
      const updatedPipelineState = setPhotosAndAttachments(pipelineState, true)
        .setIn(['listing', 'id'], listingId)
        .set(
          'associatedListing',
          fromJS({
            id: listingId,
            type: listingState.type,
            status: listingState.status,
          })
        ) as BrokeragePipeline

      const reqOpts: IRequestOptions = {
        body: JSON.stringify(updatedPipelineState),
        method: pipelineState.id ? RequestMethod.PUT : RequestMethod.POST,
      }

      const reqUrl = pipelineState.id ? `/api/BrokeragePipelines/${pipelineState.id}` : '/api/BrokeragePipelines'

      const result = await makeAuthenticatedRequest(reqUrl, reqOpts)

      if (result.success === true) {
        return result.entity.id
      }
    } catch (err) {
      console.log(err)
      dispatch(errorToast('Oops!', err.message))
    }
  }

  const onSave = async (createProperty: boolean = false, selectedProperty?: IAssociatedProperty, createListing: boolean = false, selectedListing?: IListing) => {
    setIsSaving(true)

    const propertyId = await saveProperty(selectedProperty?.propertyId)
    const listingId = await saveListing(propertyId, selectedListing?.id)

    const pipelineId = await savePipeline(listingId)
    setIsSaving(false)
    dispatch(push(`/brokerage/pipeline/${pipelineId}`))
  }

  return (
    <EditPageContainer
      title={pipelineState.id ? 'Edit Pipeline' : 'Quick Add Pipeline'}
      onSaveClick={() =>
        onSave(createProperty, selectedProperty, createListing, selectedListing)
          .then(() => dispatch(successToast('Success!', 'Pipeline saved')))
          .catch((err) => {
            console.error('Error saving:', err)
            dispatch(errorToast('Oops!', 'An error occurred while saving.'))
          })
      }
      saveDisabled={isSaving}
      onCancelClick={() => {
        dispatch(goBack())
      }}
    >
      <div className="container">
        <h3 className="pt-3 text-secondary">Property</h3>
        <hr className="border" />
      </div>
      <div className="container">
        <div className="d-flex">
        <AssociatedPropertySelector
            className="flex-grow-1 mx-2 pl-4"
            value={selectedProperty}
            onChange={(x: AssociatedProperty) => {
              if (x?.propertyId) {
                setSelectedProperty(x)
                setSelectedListing(undefined)
                setCreateListing(false)
                dispatch(listingActionCreators.loadAddListing(x.propertyId, undefined, undefined, true))
                dispatch(pipelineActionCreators.loadAddPipeline('', true))
              }
            }}
          />
          <Button
            color="primary"
            type="button"
            onClick={() => {
              setCreateSelected(true)
              if (selectedProperty) {
                dispatch(propertyActionCreators.loadAddProperty(undefined, true))
                dispatch(pipelineActionCreators.loadAddPipeline('', true))
                dispatch(listingActionCreators.loadAddListing(undefined, undefined, undefined, true))
              }
              setSelectedProperty(undefined)
            }}
          >
            Create New Property
          </Button>
        </div>
      </div>
      <PropertyEdit className="pl-5" quickAdd={true} propertyId={selectedProperty?.propertyId} />
      <Container>
        <h3 className="text-secondary">Listing</h3>
        <hr className="border" />
      </Container>
      <div className="container">
        <div className="d-flex">
          <ListingSelector
            className="flex-grow-1 mx-2 pl-4"
            value={selectedListing}
            filterPropertyId={selectedProperty?.propertyId}
            isDisabled={!selectedProperty}
            onChange={(l: Listing | undefined) => {
              if (l?.id) {
                setSelectedListing(l)
                setCreateListing(false)
                dispatch(listingActionCreators.loadEditListing(l.id))
                dispatch(pipelineActionCreators.loadAddPipeline('', true))
              }
            }}
          />
          <Button
            color="primary"
            type="button"
            onClick={() => {
              setCreateListing(true)
              if (selectedProperty) {
                dispatch(pipelineActionCreators.loadAddPipeline('', true))
                dispatch(listingActionCreators.loadAddListing(selectedProperty.propertyId, undefined, undefined, true))
              }
              setSelectedListing(undefined)
            }}
          >
            Create New Listing
          </Button>
        </div>
      </div>
      <ListingEdit
        className="pl-5"
        quickAdd={true}
        propertyId={selectedProperty?.propertyId}
        listingId={selectedListing?.id}
      />
      <Container>
        <h3 className="text-secondary">Pipeline</h3>
        <hr className="border" />
      </Container>
      <PipelineEdit className="pl-5" quickAdd={true} />
    </EditPageContainer>
  )
}
